import {
  type AudioNote,
  type CalendarEvent,
  type GoogleMeetMeeting,
  type DetectedMeeting,
  RecallDetectedMeeting,
  type MeetingAppName,
} from '@highlight/common'

// Sorts conversations by endedAt date, most recent first
export const sortConversationsByEndTime = (conversations: AudioNote[]): AudioNote[] => {
  return conversations.sort((a, b) => new Date(b.endedAt).getTime() - new Date(a.endedAt).getTime())
}

const normalizeMeetingTitle = (value: string | undefined | null): string => {
  if (!value) return ''
  return value.toLowerCase().replace(/\s+/g, ' ').trim()
}

/**
 * True when `note` is the recording for `event`. Exact match on
 * `calendarEventId` when present; otherwise falls back to time-range overlap +
 * normalized-title equality. The time-overlap requirement keeps a recurring
 * meeting's future occurrence from being paired with a prior occurrence's note.
 */
export const isNoteForCalendarEvent = (note: AudioNote, event: CalendarEvent): boolean => {
  const noteEventId = note.meeting?.calendarEventId
  if (noteEventId && event.id) return noteEventId === event.id
  if (noteEventId) return false // note is tied to a different event

  const eventTitle = normalizeMeetingTitle(event.title)
  if (!eventTitle) return false
  const noteTitle = normalizeMeetingTitle(note.title) || normalizeMeetingTitle(note.meeting?.windowTitle)
  if (!noteTitle || noteTitle !== eventTitle) return false

  const eventStart = new Date(event.start).getTime()
  const eventEnd = new Date(event.end || event.start).getTime()
  const noteStart = new Date(note.startedAt).getTime()
  const noteEnd = new Date(note.endedAt).getTime()
  return noteStart <= eventEnd && noteEnd >= eventStart
}

export const isScheduledMeeting = (
  meeting: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting,
): meeting is GoogleMeetMeeting => {
  return 'hangoutLink' in meeting
}

export const isRecallDetectedMeeting = (
  meeting: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting,
): meeting is RecallDetectedMeeting => {
  return 'windowId' in meeting
}

/**
 * The calendar event a meeting is linked to, or undefined.
 *
 * Reads `calendarEventId` off any detected meeting rather than gating on
 * object shape. An earlier version only honored it on `RecallDetectedMeeting`
 * (identified by `windowId`) and silently returned undefined for a plain
 * `DetectedMeeting` — which declares the field too — so any non-Recall
 * producer's link was dropped on the floor.
 */
export const getMeetingCalendarEventId = (
  meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting | null,
): string | undefined => {
  if (!meeting) return undefined
  // A scheduled meeting IS the calendar event — its id is the event id.
  if (isScheduledMeeting(meeting)) return meeting.id
  return meeting.calendarEventId
}

/**
 * App display name for a detected/scheduled meeting (e.g. "Huddle", "Zoom",
 * "Google Meet"). DetectedMeeting/RecallDetectedMeeting carry a typed
 * `app.name`; GoogleMeetMeeting (scheduled, identified by its hangoutLink) has
 * no app field and is always Google Meet. Returns undefined when no meeting is
 * provided.
 *
 * Type-safe across the detected-meeting union — unlike `getMeetingAppName` in
 * connections/calendars, which expects a CalendarEvent and silently returns
 * "Other" if handed one of these objects.
 */
export const getDetectedMeetingAppName = (
  meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting | null,
): MeetingAppName | undefined => {
  if (!meeting) return undefined
  if (isScheduledMeeting(meeting)) return 'Google Meet'
  return meeting.app.name
}

/**
 * Extract meeting data and title from a meeting object
 * Converts GoogleMeetMeeting to Meeting format if needed
 * For RecallDetectedMeeting with calendarEvent, uses calendar event title
 */
export const getMeetingDataFromMeeting = (
  meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting,
): { title: string; meetingData: DetectedMeeting | RecallDetectedMeeting | undefined } => {
  if (!meeting) {
    return { title: '', meetingData: undefined }
  }

  if (isScheduledMeeting(meeting)) {
    const googleMeet = meeting as GoogleMeetMeeting
    const title = googleMeet.title
    const meetingData: DetectedMeeting = {
      app: {
        name: 'Google Meet',
        titlePattern: /^$/,
      },
      appName: '',
      title,
      windowTitle: title,
      url: googleMeet.hangoutLink,
      titleMatch: '',
      attendees: googleMeet.attendees,
    }
    return { title, meetingData }
  } else if (isRecallDetectedMeeting(meeting)) {
    // RecallDetectedMeeting - use calendar event title if available
    const recallMeeting = meeting as RecallDetectedMeeting
    const calendarEvent = recallMeeting.calendarEvent
    let displayTitle: string = 'Meeting Notes' // Default title
    let meetingTitle: string = ''
    if (calendarEvent?.title) {
      displayTitle = calendarEvent.title
      meetingTitle = calendarEvent.title
    } else if (recallMeeting.title) {
      displayTitle = recallMeeting.title
      meetingTitle = recallMeeting.title
    } else if (recallMeeting.windowTitle) {
      displayTitle = recallMeeting.windowTitle
    } else if (recallMeeting.app?.name) {
      displayTitle = `${recallMeeting.app.name} Meeting Notes`
    }

    return {
      title: displayTitle,
      meetingData: {
        ...recallMeeting,
        title: meetingTitle,
      },
    }
  } else {
    const meetingObj = meeting as DetectedMeeting
    let displayTitle: string = 'Meeting Notes' // Default title
    let meetingTitle: string = ''
    if (meetingObj.title) {
      meetingTitle = meetingObj.title
      displayTitle = meetingObj.title
    } else if (meetingObj.windowTitle) {
      displayTitle = meetingObj.windowTitle
    } else if (meetingObj.app?.name) {
      displayTitle = `${meetingObj.app.name} Meeting Notes`
    }
    return {
      title: displayTitle,
      meetingData: {
        ...meetingObj,
        title: meetingTitle,
      },
    }
  }
}

/**
 * Whether a note reads as a "meeting note" in UI copy: it does exactly when it
 * is tied to a detected meeting (Zoom/Meet/Teams/Huddle). `meetingAppName` is
 * the detection-time form of the same signal — the share banner's saved note
 * carries the app name before the note's `meeting` is linked. The single
 * predicate behind `noteNoun` — label sites must not re-derive this from other
 * fields.
 */
export const isMeetingNote = (note: Pick<AudioNote, 'meeting'> & { meetingAppName?: MeetingAppName }): boolean => {
  return !!note.meeting || !!note.meetingAppName
}

/**
 * The UI noun for an audio note: "meeting note" when `isMeeting`, "audio note"
 * otherwise. Every label, tooltip, and message shares this one formatter so the
 * wording and casing can't drift between surfaces.
 */
export const noteNoun = (isMeeting: boolean, options: { plural?: boolean; capitalize?: boolean } = {}): string => {
  const noun = `${isMeeting ? 'meeting' : 'audio'} note${options.plural ? 's' : ''}`
  return options.capitalize ? noun.replace(/(^|\s)./g, (char) => char.toUpperCase()) : noun
}

/**
 * Format elapsed time from a start time to a human-readable string (HH:MM:SS or MM:SS)
 */
export const formatElapsedTime = (startTime: Date | null | undefined): string => {
  if (!startTime) {
    return '00:00'
  }

  const elapsedMs = Date.now() - startTime.getTime()
  const seconds = Math.floor((elapsedMs / 1000) % 60)
  const minutes = Math.floor((elapsedMs / (1000 * 60)) % 60)
  const hours = Math.floor(elapsedMs / (1000 * 60 * 60))

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}
